import { ensureArray } from '@libs/ensureArray'

/**
 * Transform values like "1200" to "1200px".
 */
export function toPx(value: number | string): string {
  return `${String(value).replace('px', '')}px`
}

/**
 * Transform values like "1200" to "1200%".
 */
export function toPercent(value: number): string {
  return `${String(value).replace('%', '')}%`
}

/**
 * Transform values like "1200px" or "50%" to 1200 or 50.
 */
export function valueWithUnitToNumber(value: string): number {
  return Number.parseInt(value.replace('px', '').replace('%', ''), 10)
}

/**
 * Do an addition between a value with its unit and an operand.
 * Example: '50px' + 10 => '60px'
 */
export function add(
  valueWithUnit: string,
  operand: number,
  withUnit = true
): string | number {
  const [value, unit] = _splitValue(valueWithUnit)
  return withUnit
    ? `${Number(value) + operand}${unit}`
    : Number(value) + operand
}

/**
 * Do an substraction between a value with its unit and an operand.
 * Example: '50px' - 10 => '40px'
 */
export function sub(
  valueWithUnit: string,
  operand: number,
  withUnit = true
): string | number {
  const [value, unit] = _splitValue(valueWithUnit)
  return withUnit
    ? `${Number(value) - operand}${unit}`
    : Number(value) - operand
}

/**
 * Do a division between a value with its unit and an operand.
 * Example: '50px' / 2 => '25px'
 */
export function divide(
  valueWithUnit: string,
  operand: number,
  withUnit = true
): string | number {
  const [value, unit] = _splitValue(valueWithUnit)
  return withUnit
    ? `${Number(value) / operand}${unit}`
    : Number(value) / operand
}

/**
 * Do a multiplication between a value with its unit and an operand.
 * Example: '50px' * 2 => '100px'
 */
export function multiply(
  valueWithUnit: string,
  operand: number,
  withUnit = true
): string | number {
  const [value, unit] = _splitValue(valueWithUnit)
  return withUnit
    ? `${Number(value) * operand}${unit}`
    : Number(value) * operand
}

/**
 * Reverse the value.
 */
export function reverse(
  valueWithUnit: string,
  withUnit = true
): string | number {
  const [value, unit] = _splitValue(valueWithUnit)
  return withUnit ? `${-Number(value)}${unit}` : -Number(value)
}

/**
 * Convert hexa to rvb.
 * Credits: https://stackoverflow.com/a/11508164
 */
export function hexToRgb(hex: string): [number, number, number] {
  const hexValue = hex.startsWith('#') ? hex.slice(1) : hex

  const bigint = Number.parseInt(hexValue, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return [r, g, b]
}

/**
 * Return true if the browser supports the CSS `property` and `value`.
 * Always return false if CSS.supports() is not supported by the browser (IE11 included)
 */
export function isCSSSupports(property: string, value = ''): boolean {
  try {
    return CSS.supports(property, value)
  } catch {
    return false
  }
}

function _splitValue(value: string): string[] {
  return ensureArray(value.match(/^(\d+)(\w+)$/)).slice(1)
}
